/* ============================================================
   VaporBarUS — Campaign Hero Slider
   Vanilla JS controller for the cvus-hero section.
   No dependencies.

   Behavior:
   - CSS scroll-snap handles native touch swipe positioning
   - Programmatic navigation (autoplay, dots, arrows, keyboard)
     uses a RAF tween with cubic-bezier ease-out-quart for a
     velvety, premium feel
   - Active dot shows an autoplay progress fill via CSS animation
   - Pauses on hover, focus, tab-away
   - Respects prefers-reduced-motion (instant scroll, no progress)
============================================================ */
(function () {
  'use strict';

  // cubic-bezier(.22,.61,.36,1) — ease-out-quart
  function easeOutQuart(t) {
    var u = 1 - t;
    return 1 - u * u * u * u;
  }

  function init(section) {
    var track = section.querySelector('[data-track]');
    if (!track) return;

    var slides = track.querySelectorAll('.cvus-hero__slide');
    if (slides.length <= 1) return;

    var dots = section.querySelectorAll('[data-dot]');
    var prev = section.querySelector('[data-prev]');
    var next = section.querySelector('[data-next]');

    var autoplay = section.dataset.autoplay === 'true';
    var speedMs = parseInt(section.dataset.speed || '6', 10) * 1000;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    section.style.setProperty('--cvus-progress-duration', speedMs + 'ms');
    section.dataset.paused = 'false';

    var current = 0;
    var timer = null;
    var rafId = null;
    var inView = true;

    function tweenScrollTo(targetLeft) {
      if (rafId) cancelAnimationFrame(rafId);
      var startLeft = track.scrollLeft;
      var delta = targetLeft - startLeft;
      if (Math.abs(delta) < 1) { track.scrollLeft = targetLeft; return; }
      // Don't fight the mandatory snap engine while we drive scrollLeft by hand
      // (a known iOS Safari jank source); promote the layer only for the tween.
      track.style.scrollSnapType = 'none';
      track.style.willChange = 'scroll-position';
      var duration = 520;
      var startTime = performance.now();
      function endTween() {
        rafId = null;
        track.style.scrollSnapType = '';   // restore stylesheet's `x mandatory`
        track.style.willChange = '';
      }
      function step(now) {
        var t = Math.min(1, (now - startTime) / duration);
        track.scrollLeft = startLeft + delta * easeOutQuart(t);
        if (t < 1) rafId = requestAnimationFrame(step);
        else endTween();
      }
      rafId = requestAnimationFrame(step);
    }

    function goTo(i, smooth) {
      i = ((i % slides.length) + slides.length) % slides.length;
      current = i;
      var targetLeft = slides[i].offsetLeft;
      if (smooth && !reduced) tweenScrollTo(targetLeft);
      else track.scrollLeft = targetLeft;
      updateDots();
    }

    function updateDots() {
      dots.forEach(function (dot, i) {
        if (i === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      // Restart the active dot's progress animation by forcing reflow on its fill
      var active = dots[current];
      if (active) {
        var fill = active.querySelector('.cvus-hero__dot-fill');
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }
    }

    function clearTimer() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function start() {
      clearTimer();
      // Stay paused (progress frozen, no advance) when autoplay is off, reduced
      // motion is requested, the hero is off-screen, or the mobile menu is open.
      if (!autoplay || reduced || !inView || menuOpen()) {
        section.dataset.paused = 'true';
        return;
      }
      section.dataset.paused = 'false';
      timer = setInterval(function () { goTo(current + 1, true); }, speedMs);
    }

    function stop() {
      clearTimer();
      section.dataset.paused = 'true';
    }

    function menuOpen() {
      return document.body.classList.contains('cvus-mnav-open');
    }

    if (prev) prev.addEventListener('click', function () { goTo(current - 1, true); start(); });
    if (next) next.addEventListener('click', function () { goTo(current + 1, true); start(); });
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.dataset.dot, 10), true);
        start();
      });
    });

    // Sync current index when the user scrolls/swipes manually (mobile touch)
    var scrollT;
    track.addEventListener('scroll', function () {
      // Ignore scroll events our own tween produces
      if (rafId) return;
      clearTimeout(scrollT);
      scrollT = setTimeout(function () {
        var sl = track.scrollLeft;
        var closest = 0;
        var minDist = Infinity;
        slides.forEach(function (slide, i) {
          var d = Math.abs(slide.offsetLeft - sl);
          if (d < minDist) { minDist = d; closest = i; }
        });
        if (closest !== current) { current = closest; updateDots(); }
      }, 80);
    });

    // Pause on hover/focus
    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', start);
    section.addEventListener('focusin', stop);
    section.addEventListener('focusout', start);

    // Keyboard
    section.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { goTo(current - 1, true); start(); }
      else if (e.key === 'ArrowRight') { goTo(current + 1, true); start(); }
    });

    // Pause when tab not visible
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    // Pause autoplay when the hero scrolls off-screen (saves mobile CPU/GPU;
    // the timer + RAF tween were previously running even when not visible).
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) start(); else stop();
      }, { threshold: 0.2 });
      io.observe(section);
    }

    // Pause autoplay while the mobile menu (cvus-mnav) is open, resume on close.
    // Only react to an actual open/close transition so unrelated body-class
    // churn (sticky header, cookie banner, etc.) can't reset the autoplay timer.
    if ('MutationObserver' in window) {
      var lastMenuOpen = menuOpen();
      var bodyMo = new MutationObserver(function () {
        var nowOpen = menuOpen();
        if (nowOpen === lastMenuOpen) return;
        lastMenuOpen = nowOpen;
        if (nowOpen) stop(); else start();
      });
      bodyMo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    updateDots();
    start();
  }

  function initAll() {
    document.querySelectorAll('.cvus-hero').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('.cvus-hero');
    if (s) init(s);
  });
})();
